import React, { useState, useEffect } from 'react';
import { getData, getDataById } from '../api/ApiServices';

interface Component {
    itemId: string;
    qty: number;
    item: {
        _id: string;
        hsnCode: string;
        internalPartNo: string;
        externalPartNo: string;
        partName: string;
        model: {
            _id: string;
            code: string;
            name: string;
            status: string;
        };
        category: {
            _id: string;
            code: string;
            name: string;
            status: string;
        };
        uom: {
            _id: string;
            code: string;
            name: string;
            status: string;
        };
    };
}

interface BomDetailsPopupProps {
    isOpen: boolean;
    onClose: () => void;
    bomId: string;
    parentItem?: {
        partName: string;
        internalPartNo: string;
    };
}

const customPopupStyles = `
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .popup-content {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .popup-title {
    font-size: 18px;
    font-weight: 600;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #666;
  }
  
  .close-button:hover {
    color: #333;
  }
  
  .components-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
  }
  
  .components-table th,
  .components-table td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .components-table th {
    background-color: #f5f5f5;
    font-weight: 600;
  }
  
  .components-table tr:hover {
    background-color: #f9f9f9;
  }
  
  .right-align {
    text-align: right;
  }
  
  .loader {
    text-align: center;
    padding: 20px;
  }
  
  .error-message {
    color: #d32f2f;
    padding: 15px;
    background-color: #ffebee;
    border-radius: 4px;
    margin-bottom: 15px;
  }
  
  .no-data {
    text-align: center;
    padding: 30px;
    color: #666;
  }
`;

const BomComponentsPopup: React.FC<BomDetailsPopupProps> = ({
    isOpen,
    onClose,
    bomId,
    parentItem
}) => {
    const [components, setComponents] = useState<Component[]>([]);
    const [partentPart, setParentPart] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !bomId) return;
        fetchBomComponents();
    }, [isOpen, bomId]);

    const fetchBomComponents = async () => {
        setIsLoading(true);
        try {
            const response: any = await getDataById(`/bom`, bomId);
            const data = response?.data?.components || [];
            setParentPart(response.data.item)
            setComponents(data);
        } catch (err) {
            console.error(err);
            setError('Failed to load BOM component details.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="popup-overlay" onClick={onClose}>
            <style>{customPopupStyles}</style>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                    <div className="popup-title">
                        {parentItem ?
                            `Component Details for ${parentItem.internalPartNo} - ${parentItem.partName}` :
                            'Component Details'}
                    </div>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                {error && <div className="error-message">{error}</div>}

                {isLoading ? (
                    <div className="loader">Loading component details...</div>
                ) : components.length === 0 ? (
                    <div className="no-data">No component records found for this BOM.</div>
                ) : (
                    <div>
                        <div>
                            <div className="popup-title">
                                Parent Part Details
                            </div>
                            <table className="components-table">
                                <thead>
                                    <tr>
                                        <th>HSN Code</th>
                                        <th>Internal Part No</th>
                                        <th>External Part No</th>
                                        <th>Part Name</th>
                                        <th>Model</th>
                                        <th>Category</th>
                                        <th>UOM</th>
                                    </tr>
                                </thead>
                                <tbody>

                                    <tr>
                                        <td>{partentPart?.hsnCode || 'N/A'}</td>
                                        <td>{partentPart?.internalPartNo || 'N/A'}</td>
                                        <td>{partentPart?.externalPartNo || 'N/A'}</td>
                                        <td>{partentPart?.partName || 'N/A'}</td>
                                        <td>{partentPart?.model?.name || 'N/A'}</td>
                                        <td>{partentPart?.category?.name || 'N/A'}</td>
                                        <td>{partentPart?.uom?.name || 'N/A'}</td>
                                    </tr>

                                </tbody>
                            </table>
                        </div>
                        <div>
                            <div className="popup-title">
                                Child Part Details
                            </div>
                            <table className="components-table">
                                <thead>
                                    <tr>
                                        <th>HSN Code</th>
                                        <th>Internal Part No</th>
                                        <th>External Part No</th>
                                        <th>Part Name</th>
                                        <th>Model</th>
                                        <th>Category</th>
                                        <th>UOM</th>
                                        <th className="right-align">Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {components.map((component, index) => (
                                        <tr key={index}>
                                            <td>{component.item?.hsnCode || 'N/A'}</td>
                                            <td>{component.item?.internalPartNo || 'N/A'}</td>
                                            <td>{component.item?.externalPartNo || 'N/A'}</td>
                                            <td>{component.item?.partName || 'N/A'}</td>
                                            <td>{component.item?.model?.name || 'N/A'}</td>
                                            <td>{component.item?.category?.name || 'N/A'}</td>
                                            <td>{component.item?.uom?.name || 'N/A'}</td>
                                            <td className="right-align">{component.qty}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BomComponentsPopup;